import React from "react";
import { Select } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { useChatContext } from "../context";
import type { Message } from "../types";
import type { DrawerMode } from "../utils/debugPanelUtils";
import { resolveConversationDate, formatStepsJson } from "../utils/debugPanelUtils";
import { AGENT_MODEL_OPTIONS } from "../utils";

const DebugRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="chat-v2-settings-row">
        <span className="chat-v2-settings-label">{label}</span>
        <span className="debug-row-value">{value}</span>
    </div>
);

const ConversationSection: React.FC = () => {
    const { conversationId, conversations, messages, agentModel, setAgentModel } = useChatContext();
    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);
    const date = resolveConversationDate(conversationId, conversations, messages);

    return (
        <div className="debug-section">
            <span className="debug-section-heading">Conversation</span>
            {date !== null && <DebugRow label="Started" value={date} />}
            {isStaff && (
                <div className="chat-v2-settings-row">
                    <span className="chat-v2-settings-label">Model</span>
                    <Select
                        className="chat-v2-settings-select"
                        value={agentModel ?? ""}
                        onChange={(val: string) => setAgentModel(val || null)}
                        options={AGENT_MODEL_OPTIONS}
                        size="small"
                    />
                </div>
            )}
        </div>
    );
};

const MessageSection: React.FC<{ message: Message }> = ({ message }) => {
    const { stepsByTurn } = useChatContext();
    const { user } = useAuth0();
    if (!isStaffEmail(user?.email)) return null;

    const turnId = message.metadata.turnId;
    const steps = (turnId && stepsByTurn[turnId]) || [];

    return (
        <div className="debug-section">
            <span className="debug-section-heading">Steps</span>
            {steps.length === 0 ? (
                <span className="debug-empty">No steps recorded for this turn</span>
            ) : (
                <textarea
                    className="debug-json-textarea"
                    readOnly
                    value={formatStepsJson(steps)}
                />
            )}
        </div>
    );
};

interface DebugPanelProps {
    drawerMode: DrawerMode;
    selectedMessage: Message | null;
}

const DebugPanel: React.FC<DebugPanelProps> = ({ drawerMode, selectedMessage }) => {
    if (drawerMode === "conversation") return <ConversationSection />;
    if (drawerMode === "message" && selectedMessage !== null) return <MessageSection message={selectedMessage} />;
    return null;
};

export default DebugPanel;
